var _a;
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button, ConfirmationModal, MultiColumnList, Pane, PaneMenu, Paneset, Layout, Headline, MessageBanner, } from '@folio/stripes/components';
import { useQueryClient } from 'react-query';
import { useStripes } from '@folio/stripes/core';
import { ImportBatchStatus } from '../types/api';
import useImportBatchList from '../hooks/useImportBatchList';
import useStartImport from '../hooks/useStartImport';
import { isTimeoutError } from '../utils/errorKind';
import useActiveImportBatch from '../hooks/useActiveImportBatch';
import NavPaneLayout from '../components/NavPaneLayout';
import { TableLoadingSkeleton, TableRefetchOverlay } from '../components/TableStates';
import ImportPipelineModal from '../components/ImportPipeline/ImportPipelineModal';
// CZNA-671 — COMPLETED_WITH_ERRORS sits between green and red: the run finished, but some
// records were not imported. It must never share COMPLETED's green.
var STATUS_COLORS = (_a = {},
    _a[ImportBatchStatus.COMPLETED] = '#28a745',
    _a[ImportBatchStatus.COMPLETED_WITH_ERRORS] = '#e8590c',
    _a[ImportBatchStatus.RUNNING] = '#007bff',
    _a[ImportBatchStatus.FAILED] = '#dc3545',
    _a[ImportBatchStatus.PAUSED] = '#ffc107',
    _a);
var ImportBatchList = function () {
    var intl = useIntl();
    var history = useHistory();
    var match = useRouteMatch();
    var list = useImportBatchList();
    var stripes = useStripes();
    var canStartImport = stripes.hasPerm('czna-authorities.import.execute');
    var activeBatch = useActiveImportBatch().data;
    var startImport = useStartImport();
    var queryClient = useQueryClient();
    var _a = useState(false), confirmOpen = _a[0], setConfirmOpen = _a[1];
    /** CZNA-1625 — a failed start used to say nothing at all; see `handleStartError`. */
    var _b = useState(null), startErrorId = _b[0], setStartErrorId = _b[1];
    /**
     * CZNA-1625 — a start nobody answered in time may have launched the import. The button
     * re-enabled on the stale "no active batch" and invited a second NK REPLACE import, so the
     * batch list and the active batch are re-read (a running batch disables the button again)
     * and the message asks to check the list first. Any other failure gets the plain start error
     * it never had.
     */
    var handleStartError = function (error) {
        if (isTimeoutError(error)) {
            queryClient.invalidateQueries(['czna-import-batches']);
            queryClient.invalidateQueries(['czna-import-active']);
            setStartErrorId('ui-czna-authorities.imports.start.timeout');
            return;
        }
        setStartErrorId('ui-czna-authorities.importFile.error.start');
    };
    var _c = useState(false), pipelineOpen = _c[0], setPipelineOpen = _c[1];
    var handleRowClick = useCallback(function (_e, row) {
        history.push("".concat(match.url, "/").concat(row.id));
    }, [match.url, history]);
    var visibleColumns = useMemo(function () { return [
        'startedAt',
        'sourceType',
        'fileName',
        'status',
        'totalCount',
        'importedCount',
        'updatedCount',
        'skippedCount',
        'failedCount',
    ]; }, []);
    var columnMapping = useMemo(function () { return ({
        startedAt: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.column.startedAt" }),
        sourceType: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.column.sourceType" }),
        fileName: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.column.fileName" }),
        status: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.column.status" }),
        totalCount: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.column.totalCount" }),
        importedCount: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.column.importedCount" }),
        updatedCount: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.column.updatedCount" }),
        skippedCount: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.column.skippedCount" }),
        failedCount: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.column.failedCount" }),
    }); }, []);
    var resultsFormatter = useMemo(function () { return ({
        startedAt: function (record) {
            if (!record.startedAt)
                return '';
            var date = intl.formatDate(record.startedAt, { day: '2-digit', month: '2-digit', year: 'numeric' });
            var time = intl.formatTime(record.startedAt, { hour: '2-digit', minute: '2-digit' });
            return "".concat(date, " ").concat(time);
        },
        sourceType: function (record) {
            return intl.formatMessage({ id: "ui-czna-authorities.imports.sourceType.".concat(record.sourceType) });
        },
        fileName: function (record) { return record.fileName || '—'; },
        status: function (record) { return (React.createElement("span", { style: {
                color: STATUS_COLORS[record.status] || '#333',
                fontWeight: 600,
            } }, intl.formatMessage({ id: "ui-czna-authorities.imports.status.".concat(record.status) }))); },
        totalCount: function (record) { return record.totalCount; },
        importedCount: function (record) { return record.importedCount; },
        updatedCount: function (record) { var _a; return (_a = record.updatedCount) !== null && _a !== void 0 ? _a : 0; },
        skippedCount: function (record) { return record.skippedCount; },
        failedCount: function (record) { return (React.createElement("span", { style: record.failedCount > 0 ? { color: '#dc3545', fontWeight: 600 } : undefined }, record.failedCount)); },
    }); }, [intl]);
    // CZNA-1343 — the description is for anyone who sees the list, not only who may start one.
    var paneActions = (React.createElement(PaneMenu, null,
        React.createElement(Button, { onClick: function () { return setPipelineOpen(true); }, marginBottom0: true },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.pipeline.button" })),
        canStartImport && (React.createElement(Button, { buttonStyle: "primary", disabled: !!activeBatch || startImport.isLoading, onClick: function () { return setConfirmOpen(true); }, marginBottom0: true },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.start.button" })))));
    var renderContent = function () {
        // CZNA-538 — same indication as the record lists. An admin screen still showing
        // the old corner spinner would read as a fault in the redesigned one next to it.
        if ((list.isLoading || list.isFetching) && list.items.length === 0) {
            return React.createElement(TableLoadingSkeleton, { testId: "imports-loading", columns: [4, 3, 3, 3, 3], rows: 6 });
        }
        if (list.isError) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement(Headline, { size: "medium", margin: "none" },
                        React.createElement(FormattedMessage, { id: "error.boundary.heading" })))));
        }
        if (list.items.length === 0 && !list.isLoading) {
            return (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
                React.createElement(Layout, { className: "padding-all-gutter" },
                    React.createElement(Headline, { size: "medium", margin: "none" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.list.empty" })))));
        }
        return (
        /* CZNA-538 — see AuthoritiesList: the overlay replaces MCL's corner spinner. */
        React.createElement(TableRefetchOverlay, { active: list.isFetching, testId: "imports-refetching" },
            React.createElement(MultiColumnList, { autosize: true, virtualize: true, contentData: list.items, totalCount: list.totalRecords, pagingType: "prev-next", pagingOffset: list.offset, pageAmount: list.pageSize, columnMapping: columnMapping, visibleColumns: visibleColumns, formatter: resultsFormatter, onRowClick: handleRowClick, onNeedMoreData: function (_amount, offset) { return list.setOffset(offset); } })));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Paneset, null,
            React.createElement(Pane, { defaultWidth: "20%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.pane.navigation" }) },
                React.createElement(NavPaneLayout, null)),
            React.createElement(Pane, { defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.imports.list.title" }), paneSub: list.totalRecords > 0
                    ? intl.formatMessage({ id: 'ui-czna-authorities.imports.list.subtitle' }, { count: list.totalRecords })
                    : undefined, lastMenu: paneActions },
                startErrorId && (React.createElement(MessageBanner, { type: "error" },
                    React.createElement("span", { "data-testid": "imports-start-error" },
                        React.createElement(FormattedMessage, { id: startErrorId })))),
                renderContent())),
        React.createElement(ImportPipelineModal, { open: pipelineOpen, onClose: function () { return setPipelineOpen(false); } }),
        React.createElement(ConfirmationModal, { open: confirmOpen, heading: intl.formatMessage({ id: 'ui-czna-authorities.imports.start.modal.heading' }), message: intl.formatMessage({ id: 'ui-czna-authorities.imports.start.modal.message' }), confirmLabel: intl.formatMessage({ id: 'ui-czna-authorities.imports.start.modal.confirm' }), cancelLabel: intl.formatMessage({ id: 'ui-czna-authorities.imports.start.modal.cancel' }), onConfirm: function () {
                setConfirmOpen(false);
                setStartErrorId(null);
                startImport.mutate(undefined, { onError: handleStartError });
            }, onCancel: function () { return setConfirmOpen(false); } })));
};
export default ImportBatchList;
